import { useCallback } from 'react';
import { parseDate, getRoomBooking } from '../services/Helper';
import { EventStatus, BookingCategory, RoomBooking } from '../services/Types';

/** Days after which SAVED events without updates are considered dormant (WFGE-11691) */
export const DORMANT_DAYS = 180;

interface EventLike {
  status?: string;
  updatedAt?: unknown;
  roomBooking?: RoomBooking[];
}

/**
 * Hook for dormant event business logic. Separates concerns from presentation.
 */
export const useDormant = () => {
  const cutoff = useCallback(() => {
    const d = new Date();
    d.setDate(d.getDate() - DORMANT_DAYS);
    return d;
  }, []);

  /** Returns true if event is SAVED and not updated in DORMANT_DAYS or more */
  const isDormant = useCallback((event: EventLike | null | undefined): boolean => {
    if (!event || event.status !== EventStatus.SAVED || !event.updatedAt) return false;
    const updatedAtDate = parseDate(event.updatedAt);
    if (!updatedAtDate) return false;
    return updatedAtDate < cutoff();
  }, [cutoff]);

  /**
   * Returns true if event is considered a "past event".
   * a) Has live room booking: past when event date has passed today.
   * b) No room booking (blank): past when status=SAVED AND updatedAt > 180 days ago.
   * Ensures no event dates that have passed appear in the non-past-events view.
   */
  const isPastEvent = useCallback((event: EventLike | null | undefined): boolean => {
    if (!event) return false;
    const rb = getRoomBooking(event.roomBooking, BookingCategory.LIVE);
    if (rb?.date) {
      const eventDate = new Date(Number(rb.date));
      const today = new Date();
      today.setUTCHours(0, 0, 0, 0);
      return eventDate < today;
    }
    // No room booking: past only when SAVED and not updated in 180+ days
    if (event.status === EventStatus.SAVED && event.updatedAt) {
      const updatedAtDate = parseDate(event.updatedAt);
      if (updatedAtDate) return updatedAtDate < cutoff();
    }
    return false;
  }, [cutoff]);

  /** Returns display status: DORMANT when SAVED and not updated in 180+ days (when viewing past events) */
  const getDisplayStatus = useCallback((
    event: EventLike | null | undefined,
    viewPastEvents: boolean
  ): string => {
    if (!event?.status) return event?.status || '';
    if (viewPastEvents && isDormant(event)) return 'DORMANT';
    return event.status;
  }, [isDormant]);

  return { isDormant, isPastEvent, getDisplayStatus };
};
